import React, { useState, useMemo } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: "10px",
  borderRadius: "8px",
  boxShadow: "none",
  // flexGrow: 1,
  // display: "flex",
  // flexDirection: "column",
  // height: "100%",
  // maxHeight: "calc(11 * 48px + 48px)", // ความสูงของ Table (5 แถว * ความสูงของแถว + header)
  overflowY: "auto",
  // กำหนด scrollbar แบบกำหนดเอง
  "&::-webkit-scrollbar": {
    width: "8px", // ความกว้างของ scrollbar
  },
  "&::-webkit-scrollbar-track": {
    background: "#F5F5F5", // สีเทาอ่อนมากสำหรับพื้นหลัง
    borderRadius: "8px", // มุมโค้งมน
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#CCCCCC", // สีเทากลางสำหรับ scrollbar
    borderRadius: "8px", // มุมโค้งมน
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#999999", // สีเทาเข้มเมื่อ hover
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: "linear-gradient(90deg, #00BB9F 0%, #6CD8BF 100%)",
  position: "sticky",
  top: 0, // ตำแหน่ง sticky
  zIndex: 1, // เพื่อให้แสดงอยู่ด้านบน TableBody
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: "none",
  color: "#FFFFFF",
  width: (props) => props.width || "auto", // Use width prop or default to auto
}));

const BodyTableCell = styled(TableCell)(({ theme, idx }) => ({
  backgroundColor: idx % 2 === 0 ? theme.palette.common.white : "#F8F7FD",
  paddingLeft: "16px",
  paddingRight: "16px",
  width: (props) => props.width || "auto", // Use width prop or default to auto
}));

const BodyTableCell2 = styled(TableCell)(({ theme, idx }) => ({
  backgroundColor: idx % 2 === 0 ? theme.palette.common.white : "#F1F0FA     ",
  paddingLeft: "16px",
  paddingRight: "16px",
  width: (props) => props.width || "auto", // Use width prop or default to auto
}));

// Utility function to parse custom date string
const parseCustomDateString = (dateString) => {
  const dateTimeRegex =
    /^(\d{2})\/(\d{2})\/(\d{4}) เวลา (\d{2}):(\d{2}):(\d{2}) น\.$/;
  const match = dateString.match(dateTimeRegex);

  if (!match) {
    return { formattedDate: "Invalid date", formattedTime: "" };
  }

  const [, day, month, year, hours, minutes, seconds] = match;
  const dateObj = new Date(
    `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
  );

  if (isNaN(dateObj.getTime())) {
    return { formattedDate: "Invalid date", formattedTime: "" };
  }

  const formattedDate = dateObj.toLocaleDateString("th-TH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const formattedTime = dateObj.toLocaleTimeString("th-TH", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  return { formattedDate, formattedTime };
};

const getStatus = (status) => {
  switch (status) {
    case "penalty":
      return { label: "ค่าปรับ", color: "#2196F3" };
    case "interest":
      return { label: "ดอกเบี้ย", color: "#aa00ff" };
    default:
      return { label: "ไม่ทราบสถานะ", color: "#FF0000" }; // ใช้สีแดงสำหรับสถานะที่ไม่รู้จัก
  }
};

const calculateColumnSums = (data, columns) => {
  const sums = {};
  columns.forEach((column) => {
    if (
      [
        "cost_price",
        "revenue",
        "expenditure",
        "profit_from_selling",
        "profit_from_interest",
        "profit_from_penalty",
        "profit_sum",
        // "remaining",
        "balance",
      ].includes(column.accessor)
    ) {
      sums[column.accessor] = data
        .reduce((sum, row) => sum + parseFloat(row[column.accessor] || 0), 0)
        .toFixed(2);
    }
    // ค่ารวมของคงเหลือจะต้องเป็นค่าคงเหลือสุดท้าย
    if (["remaining"].includes(column.accessor)) {
      sums[column.accessor] = data[data.length - 1]?.remaining || 0;
    }
  });

  return sums;
};

// Main component
const TableModal = ({ columns, data, hiddenColumns = [] }) => {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    const dataArray = Array.isArray(data) ? data : [];
    console.log("dataArray", dataArray);
    if (!sortColumn) return dataArray;

    return dataArray.sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      } else {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue, ["th", "en"], { sensitivity: "base" })
          : bValue.localeCompare(aValue, ["th", "en"], { sensitivity: "base" });
      }
    });
  }, [data, sortColumn, sortDirection]);

  const visibleColumns = columns.filter(
    (column) => !hiddenColumns.includes(column.accessor)
  );

  const columnSums = useMemo(
    () => calculateColumnSums(sortedData, visibleColumns),
    [sortedData, visibleColumns]
  );

  if (!Array.isArray(data)) {
    console.error("Data is not an array:", data);
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 200px)",
        overflow: "hidden",
      }}
    >
      <StyledTableContainer component={Paper}>
        <Table>
          <StyledTableHead>
            <TableRow>
              {visibleColumns.map((column) => {
                const isCenterAligned = ["balance"].includes(column.accessor);
                return (
                  <StyledTableCell
                    key={column.accessor}
                    width={column.width}
                    // onClick={() => handleSort(column.accessor)}
                    style={{
                      // cursor: "pointer",
                      textAlign: isCenterAligned ? "right" : "center",
                    }}
                    // style={{ fontWeight: "bold", textAlign: "right" }}
                  >
                    {column.Header}
                    {/* {sortColumn === column.accessor && (
                    <ArrowUpwardIcon
                      style={{
                        transform:
                          sortDirection === "asc"
                            ? "rotate(0deg)"
                            : "rotate(180deg)",
                        marginLeft: "5px",
                        fontSize: "16px",
                      }}
                    />
                  )} */}
                  </StyledTableCell>
                );
              })}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {sortedData.map((row, rowIndex) => (
              <TableRow key={row.id || rowIndex}>
                {visibleColumns.map((column, colIndex) => {
                  const accessorValue = row[column.accessor];
                  const isCenterAligned = [
                    "id",
                    "date",
                    "order_code",
                    "status",
                  ].includes(column.accessor);

                  return (
                    <BodyTableCell
                      key={colIndex}
                      idx={rowIndex}
                      width={column.width}
                      style={{
                        textAlign: isCenterAligned ? "center" : "right",
                      }}
                    >
                      {column.accessor === "index" ? (
                        rowIndex + 1
                      ) : column.accessor === "created_date" ||
                        column.accessor === "due_date" ? (
                        accessorValue ? (
                          <div>
                            {parseCustomDateString(accessorValue).formattedDate}
                            <div>
                              เวลา{" "}
                              {
                                parseCustomDateString(accessorValue)
                                  .formattedTime
                              }{" "}
                              น.
                            </div>
                          </div>
                        ) : (
                          ""
                        )
                      ) : column.accessor === "status" ? (
                        <Stack
                          direction="row"
                          spacing={1}
                          style={{
                            justifyContent: "center", // จัดตรงกลางแนวนอน
                            alignItems: "center", // จัดตรงกลางแนวตั้ง
                            width: "100%", // ให้ครอบคลุมพื้นที่ของเซลล์
                            height: "100%", // ให้ครอบคลุมพื้นที่ของเซลล์
                          }}
                        >
                          <Chip
                            label={getStatus(row.status).label}
                            style={{
                              width: "100px",
                              backgroundColor: getStatus(row.status).color,
                              color: "white",
                            }}
                          />
                        </Stack>
                      ) : column.accessor === "picture" ? (
                        Array.isArray(accessorValue) &&
                        accessorValue.length > 0 ? (
                          accessorValue.map((img, idx) => (
                            <img
                              key={idx}
                              src={img.picture}
                              alt={`รูปภาพ ${idx + 1}`}
                              style={{
                                width: "150px",
                                height: "150px",
                                objectFit: "cover",
                                objectPosition: "center",
                                margin: "0 5px",
                              }}
                            />
                          ))
                        ) : (
                          <Box
                            style={{
                              width: "150px",
                              height: "150px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#F8F7FD",
                              color: "#B0B0B0",
                            }}
                          >
                            ไม่มีรูปภาพ
                          </Box>
                        )
                      ) : column.accessor === "actions" ? (
                        column.Cell({ row })
                      ) : (
                        accessorValue || " "
                      )}
                    </BodyTableCell>
                  );
                })}
              </TableRow>
            ))}

            {/* Sticky Cell */}
            <TableRow
              style={{
                position: "sticky",
                bottom: 0, // Ensure it's sticky at the bottom of the table
                backgroundColor: "#FFFFFF", // Set a background color for the sticky row to avoid transparency
                zIndex: 1, // Ensure it's above other table rows
                // boxShadow: "none",
                borderBottom: "none",
              }}
            >
              {visibleColumns.map((column, colIndex) => {
                // กำหนดการจัดตำแหน่ง (alignment) ของข้อความ
                const isCenterAligned = ["id", "date", "order_code"].includes(
                  column.accessor
                );

                return (
                  <BodyTableCell2
                    key={colIndex}
                    style={{
                      fontWeight: "bold",
                      textAlign: isCenterAligned ? "center" : "right", // เปลี่ยนการจัดตำแหน่งตามคอลัมน์
                      boxShadow: "none",
                    }}
                  >
                    {column.accessor === "date"
                      ? "รวม"
                      : columnSums[column.accessor] || ""}
                  </BodyTableCell2>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </StyledTableContainer>
    </Box>
  );
};

export default TableModal;
